import { useState } from 'react'
import { AlertTriangle, Wrench, Settings, TrendingUp, DollarSign } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useGetDashboardAnalyticsQuery, useGetRealtimeDataQuery, useGetAlertsQuery, useGetWorkOrdersQuery, useGetEquipmentQuery } from '@/store/api'
import { useSelector } from 'react-redux'
import { selectUser } from '@/store/slices/authSlice'
import DashboardMetricCard from '@/components/dashboard/DashboardMetricCard'
import RecentAlertsCard from '@/components/dashboard/RecentAlertsCard'
import ActiveWorkOrdersCard from '@/components/dashboard/ActiveWorkOrdersCard'
import EquipmentHealthOverview from '@/components/dashboard/EquipmentHealthOverview'

export default function DashboardPage() {
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('24h')
  
  // Fetch dashboard data
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useGetDashboardAnalyticsQuery({ timeframe })
  const { data: realtimeData, isLoading: realtimeLoading } = useGetRealtimeDataQuery()
  const { data: alertsData, isLoading: alertsLoading } = useGetAlertsQuery({ 
    limit: 5
  })
  const { data: workOrdersData, isLoading: workOrdersLoading } = useGetWorkOrdersQuery({ 
    limit: 5, 
    status: ['pending', 'assigned', 'in_progress'], 
    sortBy: 'created_at', 
    sortOrder: 'desc' 
  })
  const { data: equipmentData } = useGetEquipmentQuery({ limit: 100 })

  const metrics = analyticsData?.data?.metrics
  const realtime = realtimeData?.data
  const alerts = alertsData?.data || []
  const workOrders = workOrdersData?.data || []
  const equipment = equipmentData?.data || []

  const isLoading = analyticsLoading || realtimeLoading


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'User'}
          </h1>
          <p className="text-lg text-gray-600">
            Here's an overview of your predictive maintenance operations
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-3">
          <select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-trust-blue focus:border-trust-blue"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <Button onClick={() => navigate('/analytics')}>
            <TrendingUp className="w-4 h-4 mr-2" />
            View Analytics
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                  <div className="w-16 h-6 bg-gray-200 rounded"></div>
                </div>
                <div className="space-y-2">
                  <div className="w-20 h-8 bg-gray-200 rounded"></div>
                  <div className="w-24 h-4 bg-gray-200 rounded"></div>
                  <div className="w-16 h-3 bg-gray-200 rounded"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <DashboardMetricCard
            title="Critical Alerts"
            value={metrics?.criticalAlerts || realtime?.alerts?.critical || 0}
            icon={AlertTriangle}
            iconColor="text-critical-red"
            iconBg="bg-red-100"
            badge={{
              text: `${realtime?.alerts?.new || 0} New`,
              variant: 'critical'
            }}
            trend={{
              value: 12, // This would come from analytics comparison
              isGoodWhenUp: false
            }}
            onClick={() => navigate('/alerts?priority=critical')}
          />

          <DashboardMetricCard
            title="Active Work Orders"
            value={metrics?.activeWorkOrders || realtime?.workOrders?.active || 0}
            icon={Wrench}
            iconColor="text-info-blue"
            iconBg="bg-blue-100"
            badge={{
              text: `${realtime?.workOrders?.overdue || 0} Overdue`,
              variant: realtime?.workOrders?.overdue ? 'critical' : 'info'
            }}
            trend={{
              value: -3,
              isGoodWhenUp: false
            }}
            onClick={() => navigate('/work-orders?status=active')}
          />

          <DashboardMetricCard
            title="Equipment Health"
            value={`${Math.round(realtime?.equipment?.avgHealth || 85)}%`}
            icon={Settings}
            iconColor="text-success-green"
            iconBg="bg-emerald-100"
            badge={{
              text: `${realtime?.equipment?.critical || 0} Critical`,
              variant: realtime?.equipment?.critical ? 'critical' : 'success'
            }}
            trend={{
              value: 2.5,
              isGoodWhenUp: true
            }}
            onClick={() => navigate('/equipment')}
          />

          <DashboardMetricCard
            title="Monthly Costs"
            value={`$${((realtime?.costs?.thisMonth || 12500) / 1000).toFixed(1)}K`}
            icon={DollarSign}
            iconColor="text-warning-amber"
            iconBg="bg-amber-100"
            badge={{
              text: `${Math.abs(realtime?.costs?.trend || 15)}% ${(realtime?.costs?.trend || -15) >= 0 ? 'Up' : 'Saved'}`,
              variant: (realtime?.costs?.trend || -15) >= 0 ? 'warning' : 'success'
            }}
            trend={{
              value: realtime?.costs?.trend || -15,
              isGoodWhenUp: false
            }}
            onClick={() => navigate('/analytics?tab=costs')}
          />
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts */}
        <RecentAlertsCard 
          alerts={alerts} 
          isLoading={alertsLoading} 
          onViewAll={() => navigate('/alerts')}
        />

        {/* Active Work Orders */}
        <ActiveWorkOrdersCard 
          workOrders={workOrders} 
          isLoading={workOrdersLoading} 
          onViewAll={() => navigate('/work-orders')}
          equipment={equipment}
        />
      </div>

      {/* Equipment Health Overview */}
      <EquipmentHealthOverview 
        healthData={metrics?.equipmentHealth} 
        isLoading={analyticsLoading}
        onViewEquipment={(status) => navigate(`/equipment?status=${status}`)}
      />

      {/* Error State */}
      {analyticsError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-medium text-red-800">Unable to load dashboard data</h3>
                <p className="text-sm text-red-600">Please check your connection and try again.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}